import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, Users, ShieldAlert, Bell, Boxes, ScrollText, type LucideIcon } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';

// Ricerca globale "tipo Spotlight" (richiesta dell'utente, 26/10/2026): Ctrl/Cmd+K da qualunque
// pagina, cerca fra pagine della console, tenant, incident, alert, asset e log. Non c'è un
// endpoint unico: si interrogano in parallelo le API delle liste con il loro filtro `search`,
// nello scope del tenant selezionato. Finestre limitate per restare veloci: incident e alert
// ultimi 30 giorni, log ultime 24 ore (ClickHouse cerca anche nel raw); gli asset arrivano
// interi una volta per apertura e si filtrano qui.
export interface SearchPage { to: string; label: string; icon: LucideIcon }

interface Result { key: string; group: string; icon: LucideIcon; title: string; detail?: string; go: () => void }

const MIN_CHARS = 2;
const PER_GROUP = 5;

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

export default function GlobalSearch({ pages }: { pages: SearchPage[] }) {
  const navigate = useNavigate();
  const { tenants, selectedTenantId, setSelectedTenantId } = useTenantScope();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState<Result[]>([]);
  const [searching, setSearching] = useState(false);
  const [assets, setAssets] = useState<any[] | null>(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ(''); setRemote([]); setActive(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    // Gli asset non hanno filtro lato server: una sola lettura per apertura. 403 per i viewer.
    axios.get('/api/assets', { params: { tenantId: selectedTenantId ?? undefined } })
      .then(res => setAssets(res.data.data)).catch(() => setAssets([]));
  }, [open, selectedTenantId]);

  function close() { setOpen(false); }
  function go(fn: () => void) { return () => { close(); fn(); }; }

  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < MIN_CHARS) { setRemote([]); setSearching(false); return; }
    const id = ++requestId.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      const tenantId = selectedTenantId ?? undefined;
      const [cases, alerts, events] = await Promise.allSettled([
        axios.get('/api/cases', { params: { tenantId, search: term, fromDate: daysAgoIso(30) } }),
        axios.get('/api/alerts', { params: { tenantId, search: term, fromDate: daysAgoIso(30), limit: PER_GROUP } }),
        axios.get('/api/events', { params: { tenantId, search: term, fromDate: daysAgoIso(1), limit: PER_GROUP } }),
      ]);
      if (id !== requestId.current) return;
      const rows = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? (r.value.data.data as any[]) : []);
      setRemote([
        ...rows(cases).slice(0, PER_GROUP).map(c => ({
          key: `case-${c.id}`, group: 'Incidents', icon: ShieldAlert, title: c.title,
          detail: [`INC-${c.id}`, c.severity, c.entity_host, c.tenant_name].filter(Boolean).join(' · '),
          go: go(() => navigate(`/incidents/${c.id}`)),
        })),
        ...rows(alerts).slice(0, PER_GROUP).map(a => ({
          key: `alert-${a.id}`, group: 'Alerts', icon: Bell, title: a.rule_name || a.message,
          detail: [a.host, a.src_ip, new Date(a.created_at).toLocaleString('en-GB'), a.tenant_name].filter(Boolean).join(' · '),
          go: go(() => navigate(`/alerts/${a.id}`)),
        })),
        ...(rows(events).length > 0 ? [{
          key: 'events', group: 'Logs', icon: ScrollText, title: `Search "${term}" in Security Logs`,
          detail: rows(events).slice(0, 2).map(e => `${e.host}: ${String(e.message).slice(0, 60)}`).join('  ·  '),
          go: go(() => navigate(`/logs?q=${encodeURIComponent(term)}`)),
        }] : []),
      ]);
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open, selectedTenantId]);

  const term = q.trim().toLowerCase();
  const local: Result[] = term.length === 0 ? [] : [
    ...pages.filter(p => p.label.toLowerCase().includes(term)).slice(0, PER_GROUP).map(p => ({
      key: `page-${p.to}`, group: 'Pages', icon: p.icon, title: p.label, go: go(() => navigate(p.to)),
    })),
    ...tenants.filter(t => t.name.toLowerCase().includes(term)).slice(0, PER_GROUP).map(t => ({
      key: `tenant-${t.id}`, group: 'Tenants', icon: Users, title: t.name, detail: 'Open tenant overview',
      go: go(() => { setSelectedTenantId(t.id); navigate('/overview'); }),
    })),
    ...(term.length >= MIN_CHARS ? (assets ?? []).filter(a =>
      [a.hostname, a.ip_address, a.mac_address, a.label].some(v => typeof v === 'string' && v.toLowerCase().includes(term)),
    ).slice(0, PER_GROUP).map(a => ({
      key: `asset-${a.id}`, group: 'Assets', icon: Boxes, title: a.hostname,
      detail: [a.ip_address, a.mac_address, a.tenant_name].filter(Boolean).join(' · '),
      go: go(() => navigate(`/asset-center/${a.id}`)),
    })) : []),
  ];
  const results = [...local, ...remote];

  useEffect(() => { setActive(0); }, [q]);

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) results[active].go();
  }

  return (
    <>
      <button onClick={() => setOpen(true)} title="Search everything (Ctrl+K)"
        className="flex items-center gap-2 w-10 xl:w-64 justify-center xl:justify-start px-3 py-2 rounded-lg text-sm text-gray-400 bg-gray-800/60 hover:bg-gray-800 hover:text-white border border-gray-700 cursor-pointer transition-colors">
        <Search size={15} className="shrink-0" />
        <span className="hidden xl:inline">Search…</span>
        <kbd className="hidden xl:inline ml-auto text-[10px] px-1 rounded border border-gray-600 text-gray-400">Ctrl K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh]" onMouseDown={close}>
          <div className="w-full max-w-xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 border-b border-gray-100">
              <Search size={18} className="text-gray-400" />
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onInputKey}
                placeholder="Search pages, tenants, incidents, alerts, assets, logs…"
                className="flex-1 py-3.5 text-base outline-none" />
              {searching && <span className="text-xs text-gray-400">Searching…</span>}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {results.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-gray-400">
                  {term.length === 0 ? 'Type to search across the console.' : searching ? 'Searching…' : 'No results.'}
                </p>
              ) : results.map((r, i) => (
                <div key={r.key}>
                  {(i === 0 || results[i - 1].group !== r.group) && (
                    <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{r.group}</p>
                  )}
                  <button onClick={r.go} onMouseMove={() => setActive(i)}
                    className={`flex items-center gap-3 w-full px-4 py-2 text-left ${i === active ? 'bg-primary-50' : ''}`}>
                    <r.icon size={15} className="shrink-0 text-gray-400" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-gray-900 truncate">{r.title}</span>
                      {r.detail && <span className="block text-xs text-gray-500 truncate">{r.detail}</span>}
                    </span>
                    {i === active && <CornerDownLeft size={13} className="shrink-0 text-gray-400" />}
                  </button>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400">
              Incidents and alerts: last 30 days · Logs: last 24 hours · scope: {selectedTenantId ? 'selected tenant' : 'all tenants'} · ↑↓ to move, Enter to open, Esc to close
            </div>
          </div>
        </div>
      )}
    </>
  );
}
